import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Contact from "@/pages/Contact";
import PrintAndPlay from "@/pages/PrintAndPlay";
import TeamSetup from "@/pages/TeamSetup";
import GameBoard from "@/pages/GameBoard";
import Rules from "@/pages/Rules";
import Gallery from "@/pages/Gallery";
import PvPSetup from "@/pages/PvPSetup";
import OnlineLobby from "@/pages/OnlineLobby";
import DraftPage from "@/pages/DraftPage";
import PlayerDraftPage from "@/pages/PlayerDraftPage";
import Minigames from "@/pages/Minigames";
import { syncDiagnosticsFlag } from "@/utils/diagnostics";

function Analytics() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) window.gtag("event", "page_view", { page_path: location.pathname });
  }, [location.pathname]);
  return null;
}

// One-time cleanup: prune per-game chip/card-order entries from localStorage.
// These were accidentally written there with per-game UUID keys that never got
// cleaned up, eventually filling the quota and tripping QuotaExceededError on
// render. The new code uses sessionStorage; this purges the historical bloat.
function pruneLegacyOrderKeys() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("chipOrder-") || key.startsWith("cardOrder-"))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore — best-effort
  }
}

// Legacy `/deck-builder-setup` redirect — the deck-builder lobby now lives
// inside `/online-setup` behind a Game Settings toggle. `?deck=1` tells the
// merged lobby to auto-enable the draft step. Preserves any `?game=&role=`
// already on the URL so a mid-session reload still lands in the right game.
function DeckBuilderSetupRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("deck", "1");
  return <Navigate to={`/online-setup?${next}`} replace />;
}

function App() {
  useEffect(() => { pruneLegacyOrderKeys(); syncDiagnosticsFlag(); }, []);
  return (
    <div className="App min-h-screen bg-background paper-texture">
      <BrowserRouter>
        <Analytics />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* /play was the old "name two teams" stub flow (Home.jsx). Removed in favor
              of the richer PvPSetup. Redirect preserves bookmarks + any external links. */}
          <Route path="/play" element={<Navigate to="/online-setup" replace />} />
          <Route path="/pvp-setup" element={<Navigate to="/online-setup" replace />} />
          <Route path="/online-setup" element={<PvPSetup onlineMode />} />
          <Route path="/bot-setup" element={<PvPSetup botMode />} />
          <Route path="/bot-vs-bot-setup" element={<PvPSetup botVsBotMode />} />
          <Route path="/deck-builder-setup" element={<DeckBuilderSetupRedirect />} />
          <Route path="/deck-builder-draft" element={<DraftPage />} />
          <Route path="/player-draft" element={<PlayerDraftPage />} />
          <Route path="/minigames" element={<Minigames />} />
          <Route path="/online/:gameId" element={<OnlineLobby />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/print-and-play" element={<PrintAndPlay />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/setup/:gameId" element={<TeamSetup />} />
          <Route path="/game/:gameId" element={<GameBoard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
